import { Body, Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { useParams } from "react-router";
import { getFormalityRequestRevisionExpired } from "@/queries/formality-request";
import { useEffect, useState, useMemo } from "react";
import { useInfo } from "../../hooks/use-info";
import { useGetExecution } from "@/queries/flows/flows.api";
import dayjs from "dayjs";
import { ALLOWED_NODE_TYPES } from "../node-list/node-list-filters";
import { shouldShowContinueButton as getShouldShowContinueButton } from "@/utils/procedures-ui";
import { useContinuePaymentRequest } from "@/queries/payment/payment.api";
import { useIsFetching } from "@tanstack/react-query";
import { NodeList } from "../node-list/node-list";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import ProgressBar from "./progress-bar";

export const ProcedureFormsList = () => {
  const params = useParams<{ procedureId: string }>();
  const { hasPermission } = useAuthorization();
  const canViewFlow = hasPermission(Permissions.flows.read);
  const {
    setShouldShowInfo,
    setCanBeCancelled,
    formalityRequest,
    stopIntervalChecks,
  } = useInfo();
  const [isExpired, setIsExpired] = useState(false);
  const [isProcedureCorrupted, setIsProcedureCorrupted] = useState(false);

  const {
    data: nodesExecutions,
    isLoading,
    isFetching,
  } = useGetExecution(
    formalityRequest?.flow_execution_id as string,
    stopIntervalChecks ? 0 : 5000,
    undefined,
    canViewFlow,
  );

  const isExecutionLoading = isLoading || isFetching;

  useEffect(() => {
    if (nodesExecutions?.data?.node_executions?.some(({ node }) => !node)) {
      setIsProcedureCorrupted(true);
    }
  }, [nodesExecutions]);

  useEffect(() => {
    let mounted = true;

    getFormalityRequestRevisionExpired(params.procedureId ?? "").then(
      (data) => {
        if (!mounted) return;
        if (data.length && isExpired === false) {
          setIsExpired(true);
        }
      },
    );

    return () => {
      mounted = false;
    };
  }, [params.procedureId, isExpired]);

  useEffect(() => {
    setShouldShowInfo(true);
  }, [setShouldShowInfo]);

  // NODE_EXECUTIONS PROCESADOS (sin duplicados)
  const uniqueExecutionNodes = useMemo(() => {
    const executions = nodesExecutions?.data?.node_executions ?? [];
    type Execution = (typeof executions)[number];

    const pickBetter = (
      existing: Execution,
      incoming: Execution,
    ): Execution => {
      const existingCompleted = existing.status === "COMPLETED";
      const incomingCompleted = incoming.status === "COMPLETED";
      if (existingCompleted !== incomingCompleted)
        return incomingCompleted ? incoming : existing;
      return dayjs(incoming.started_at || 0).isAfter(
        dayjs(existing.started_at || 0),
      )
        ? incoming
        : existing;
    };

    const mergeAssignments = (
      chosen: Execution,
      other: Execution,
    ): Execution => {
      const formAssignments = chosen.form_assignments?.length
        ? chosen.form_assignments
        : other.form_assignments;
      const userFormAssignments = chosen.user_form_assignments?.length
        ? chosen.user_form_assignments
        : other.user_form_assignments;
      if (
        formAssignments === chosen.form_assignments &&
        userFormAssignments === chosen.user_form_assignments
      )
        return chosen;
      return {
        ...chosen,
        form_assignments: formAssignments,
        user_form_assignments: userFormAssignments,
      };
    };

    const byId = executions
      .filter((node) => ALLOWED_NODE_TYPES.has(node?.node?.ref_name || ""))
      .reduce<Record<string, Execution>>((acc, node) => {
        const key = `${node.node.id}:${node.flow_execution_id}`;

        if (node.node.ref_name === "payment-node" && !node.payment_request) {
          const related = Object.values(acc).find(
            (e) => e.node.id === node.node.id && e.payment_request,
          );
          if (related?.node?.id) {
            acc[related.node.id] = {
              ...node,
              payment_request: related.payment_request,
            };
          }
          return acc;
        }

        const existing = acc[key];
        if (!existing) {
          acc[key] = node;
          return acc;
        }

        const chosen = pickBetter(existing, node);
        const other = chosen === existing ? node : existing;
        acc[key] = mergeAssignments(chosen, other);
        return acc;
      }, {});

    return Object.values(byId).sort((a, b) =>
      dayjs(a.started_at || 0).diff(dayjs(b.started_at || 0)),
    );
  }, [nodesExecutions]);

  // Verificar si hay un nodo de resolución ejecutado
  useEffect(() => {
    const hasExecutedResolutionNode =
      nodesExecutions?.data?.node_executions.some(
        (node) =>
          node?.node?.ref_name === "resolution-node" &&
          node?.status === "COMPLETED",
      );
    setCanBeCancelled(!hasExecutedResolutionNode);
  }, [nodesExecutions, setCanBeCancelled]);

  const { shouldShowContinueButton, paymentRequestId } = useMemo(
    () =>
      getShouldShowContinueButton(
        uniqueExecutionNodes,
        nodesExecutions?.data?.status,
      ),
    [uniqueExecutionNodes, nodesExecutions?.data?.status],
  );

  const { mutateAsync: continuePayment, isPending: isContinuing } =
    useContinuePaymentRequest(formalityRequest?.flow_execution_id ?? undefined);

  // Verificar si las queries están siendo refetched después de invalidar
  const isRefetchingFlowExecution = useIsFetching({
    queryKey: ["flow-execution", formalityRequest?.flow_execution_id],
  });
  const isRefetchingPaymentRequest = useIsFetching({
    queryKey: ["payment-request", paymentRequestId],
  });
  const isRefetchingQueries =
    (isRefetchingFlowExecution > 0 || isRefetchingPaymentRequest > 0) &&
    !isContinuing;

  if (isProcedureCorrupted) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-center">
          <Icon
            type="far"
            name="circle-exclamation"
            className="text-warning text-4xl"
          />
        </div>
        <Heading variant="h4" className="text-center">
          Trámite con datos inconsistentes
        </Heading>

        <Body>
          Hemos detectado que este trámite tiene datos inconsistentes que
          impiden mostrar la información correctamente. Por favor, contacta al
          soporte para resolver este problema.
        </Body>
        <Body>
          Una de las posibles razones de esta inconsistencia es que se hayan
          eliminado nodos del trámite después de que se hayan ejecutado. Para
          evitar este tipo de problemas, te recomendamos revisar el trámite y
          asegurarte de no eliminar nodos que ya han sido ejecutados.
        </Body>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ProgressBar />
      <NodeList
        isExecutionLoading={isExecutionLoading}
        isExpired={isExpired}
        procedureId={params.procedureId}
        uniqueExecutionNodes={uniqueExecutionNodes}
        formalityRequest={formalityRequest}
      />
      {shouldShowContinueButton && (
        <div className="mt-4 flex justify-end">
          <Button
            size="lg"
            onClick={async () => {
              if (!paymentRequestId) return;

              await continuePayment(paymentRequestId);
            }}
            isLoading={isContinuing || isRefetchingQueries}
            disabled={!paymentRequestId || isContinuing || isRefetchingQueries}
          >
            Continuar tramite
          </Button>
        </div>
      )}
    </div>
  );
};
